import React from 'react';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import Button from '@/components/ui/Button';
import axios from 'axios';
import {useFormik} from 'formik';
import {useStoreActions, useStoreState} from 'easy-peasy';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {buildURL, buildEventURL} from '@/utils/urlBuilder';
import i18n from 'i18n';
import {useDConstants} from '@/hooks/useConfig';

interface LanguageOption {
    value: string;
    label: string;
}

interface EventDetails {
    id: string;
    lang: string;
    eventType: {
        tableName: string;
    };
}

interface FormValues {
    id: string;
    lang: string;
}

interface EventLangChangeFormProps {
    onDone: () => void;
    eventTypeId: string;
}

interface ApiResponse {
    event: EventDetails;
}

interface StoreState {
    eventDetail: {
        eventDetails: EventDetails;
    };
}

interface StoreActions {
    eventDetail: {
        setEventDetails: (event: EventDetails) => void;
    };
}

const EventLangChangeForm: React.FC<EventLangChangeFormProps> = React.memo((
    {onDone, eventTypeId},
) => {
    const {dconstants, isLoaded: configLoaded} = useDConstants();
    const [langFieldChoices, loading] = useData<LanguageOption[]>(
        buildURL('langFieldChoices', {obj_id: eventTypeId}),
        'langFieldChoices'
    );

    const eventDetails = useStoreState<StoreState>((state) => state.eventDetail.eventDetails);
    const storeEventDetails = useStoreActions<StoreActions>((state) => state.eventDetail.setEventDetails);

    const onSubmit = (values: FormValues) => {
        const requestMethod = axios.patch;
        const url = buildEventURL('eventDetails', eventDetails.id);

        requestMethod<ApiResponse>(url, {
            ...values,
        })
            .catch((error) => {
                throw error;
            })
            .then((response) => {
                onDone();
                storeEventDetails(response.data.event);
            });
    };

    const formik = useFormik<FormValues>({
        initialValues: {
            id: eventDetails.id,
            lang: eventDetails.lang,
        },
        onSubmit,
    });

    let languageDisplay: React.ReactNode;
    if (loading) {
        languageDisplay = <CellLoader fullHeight={true} />;
    } else {
        languageDisplay = (
            <Form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                    <Col xs={12}>
                        {
                            eventDetails.eventType.tableName === 'call' ? (
                                <div className="alert alert-warning">
                                    {i18n.t('language_change_warning.text', {days: configLoaded ? dconstants.monitorLastDays : '60'})}
                                </div>
                            ) : null
                        }
                        <Form.Group className="mb-3" controlId="eventLang" data-cy="change-language" >
                            <Form.Label>
                                {i18n.t('jt_change_language.text')}
                            </Form.Label>
                            <Select<LanguageOption>
                                value={langFieldChoices.find((x) => x.value === formik.values.lang)}
                                onChange={(v) => formik.setFieldValue('lang', v?.value || '')}
                                onBlur={formik.handleBlur}
                                name="lang"
                                isSearchable={false}
                                options={langFieldChoices}
                                aria-label="change-language-select"
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="eventLang-save" >
                            <Button
                                variant="info"
                                size="sm"
                                type="submit"
                                data-cy="language-change-save-button"
                            >
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Form.Group>
                    </Col>
                </Row >
            </Form >
        );
    }

    return (
        <>
            {languageDisplay}
        </>
    );
});

export default EventLangChangeForm; 